import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Plus, AlertTriangle } from "lucide-react";
import { getMyDisputes } from "../../services/disputeService";
import Loader from "../../components/common/Loader";
import Button from "../../components/common/Button";
import EmptyState from "../../components/common/EmptyState";
import DisputeCard from "../../components/disputes/DisputeCard";
import { color } from "../../styles/theme";

const PAGE_SIZE = 10;

/**
 * Disputes — ported from mobile's dispute-list screen
 * (src/Screens/dispute-list).
 */
const DisputeList = () => {
  const { t } = useTranslation(["disputes", "common"]);
  const navigate = useNavigate();
  const [disputes, setDisputes] = useState([]);
  const [meta, setMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = (pageNumber = 1) => {
    (pageNumber === 1 ? setLoading : setLoadingMore)(true);
    getMyDisputes({ pageNumber, pageSize: PAGE_SIZE })
      .then((res) => {
        const list = res.data?.Result || [];
        setDisputes((prev) => (pageNumber === 1 ? list : [...prev, ...list]));
        setMeta(res.data?.Metadata || null);
        setPage(pageNumber);
      })
      .catch(() => toast.error(t("disputeList.loadError")))
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
      });
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasMore = meta && disputes.length < (meta.TotalCount ?? meta.totalCount ?? 0);

  if (loading) return <Loader />;

  return (
    <div>
      <div style={s.headerRow}>
        <h2 style={s.title}>{t("disputeList.title")}</h2>
        <Button icon={Plus} onClick={() => navigate("/disputes/new")}>{t("disputeList.newDispute")}</Button>
      </div>

      {disputes.length === 0 ? (
        <EmptyState icon={AlertTriangle} text={t("disputeList.emptyState")} />
      ) : (
        <div style={s.list}>
          {disputes.map((d) => <DisputeCard key={d.ClaimId} item={d} />)}
        </div>
      )}

      {hasMore && (
        <div style={{ textAlign: "center", marginTop: 8 }}>
          <button type="button" className="btn btn-secondary" onClick={() => load(page + 1)} disabled={loadingMore}>
            {loadingMore ? t("common:status.loading") : t("disputeList.loadMore")}
          </button>
        </div>
      )}
    </div>
  );
};

const s = {
  headerRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 10 },
  title: { fontSize: 20, fontWeight: 800, color: color.textPrimary, margin: 0, letterSpacing: -0.3 },
  list: { maxWidth: 560 },
};

export default DisputeList;
